import { type ChangeEvent, type FC, useEffect, useState } from 'react'
import { useGetEnrollmentFixedIncomeData, useSubmitEnrollmentFixedIncomeData } from '@egw/api'
import { Logger } from '@egw/logger'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { WEALTH_ROUTES } from '@egw/constants'
import {
  type FixedIncomeSectorAllocationInput,
  type EnrollmentFixedIncomeRecommendationOutput,
} from '@egw/api/src/models/enrollment/enrollment'
import { ROUTES } from '../../../constants'
import { FixedIncome } from '../../../components/fixed-income/fixed-income'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Fixed Income')

export const FixedIncomePage: FC = () => {
  const { navigate } = useRouter()

  const { data, isLoading, error } = useGetEnrollmentFixedIncomeData()
  const { mutate: submit, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentFixedIncomeData()

  const fixedIncomeCategory = data?.enrollmentData.recommendation.investment.fixedIncomeCategory
  const riskTolerance = data?.enrollmentData.userInfo.investment.riskTolerance

  const [inputData, setInputData] = useState<EnrollmentFixedIncomeRecommendationOutput[] | undefined>(
    fixedIncomeCategory,
  )

  const onChangeSliders = (index: number, event: ChangeEvent<HTMLInputElement>): void => {
    const newValue = Number(event.target.value)

    setInputData((prevState) =>
      prevState?.map((slider, i) =>
        i === index
          ? {
              ...slider,
              recommendedRange: {
                ...slider.recommendedRange,
                recommendedPercent: newValue,
              },
            }
          : slider,
      ),
    )
  }

  const handleContinueClick = async (): Promise<void> => {
    const fixedIncomeSectorAllocation = inputData?.map((item) => ({
      fixedIncomeCategory: item.type,
      allocationPercent: item.recommendedRange.recommendedPercent,
    }))

    try {
      const response = await submit({
        investmentProfile: {
          fixedIncomeCategoryAllocation: fixedIncomeSectorAllocation as FixedIncomeSectorAllocationInput[],
        },
      })
      if (response?.path) {
        navigate(response.path)
      }
    } catch (err) {
      log.error('Error submitting enrollment intro data', err)
    }
  }

  useValidateRoute(data?.path)

  useEffect(() => {
    if (fixedIncomeCategory) {
      setInputData(fixedIncomeCategory)
    }
  }, [fixedIncomeCategory])

  return (
    <EnrollmentLayout className="bg-background-light" title="Fixed Income">
      <FixedIncome
        riskTolerance={riskTolerance}
        sliderData={inputData}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        error={error}
        submitError={submitError}
        onChangeSliders={onChangeSliders}
        handleContinueClick={() => void handleContinueClick()}
      />
    </EnrollmentLayout>
  )
}
